import { apiGet } from '../http/request';

const LAYER_ORDER = ['G', 'S', 'L', 'M', 'W', 'B', 'P', 'T', 'C', 'R'];

export interface TaxonomyLayerMap {
  [layerCode: string]: string;
}

interface TaxonomyCategoryResponseItem {
  code: string;
  name: string;
  numericCode: string | number;
}

interface TaxonomySubcategoryResponseItem {
  code: string;
  name: string;
  numericCode: string | number;
}

export interface TaxonomySubcategoryNode {
  code: string;
  name: string;
  numericCode: number;
}

export interface TaxonomyCategoryNode {
  code: string;
  name: string;
  numericCode: number;
  subcategories: TaxonomySubcategoryNode[];
}

export interface TaxonomyLayerNode {
  code: string;
  name: string;
  categories: TaxonomyCategoryNode[];
}

export interface TaxonomyTreeResponse {
  layers: TaxonomyLayerNode[];
}

export interface TaxonomyPreloadOptions {
  maxConcurrentSubcategoryRequests?: number;
}

function normalizeNumericCode(value: string | number): number {
  if (typeof value === 'number') {
    return value;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function compareByCodeAndNumber(
  left: { code: string; numericCode: number },
  right: { code: string; numericCode: number }
): number {
  if (left.numericCode !== right.numericCode) {
    return left.numericCode - right.numericCode;
  }

  return left.code.localeCompare(right.code);
}

function sortLayerCodes(codes: string[]): string[] {
  return [...codes].sort((left, right) => {
    const leftIndex = LAYER_ORDER.indexOf(left);
    const rightIndex = LAYER_ORDER.indexOf(right);

    if (leftIndex === -1 && rightIndex === -1) {
      return left.localeCompare(right);
    }

    if (leftIndex === -1) {
      return 1;
    }

    if (rightIndex === -1) {
      return -1;
    }

    return leftIndex - rightIndex;
  });
}

class TaxonomyTreeService {
  private static readonly DEFAULT_SUBCATEGORY_PRELOAD_CONCURRENCY = 3;

  private layersCache: TaxonomyLayerMap | null = null;
  private categoriesCache = new Map<string, TaxonomyCategoryNode[]>();
  private subcategoriesCache = new Map<string, TaxonomySubcategoryNode[]>();
  private taxonomyTreeCache: TaxonomyTreeResponse | null = null;

  async loadLayers(): Promise<TaxonomyLayerMap> {
    if (this.layersCache) {
      return this.layersCache;
    }

    const response = await apiGet<TaxonomyLayerMap>('/taxonomy/layers');
    this.layersCache = response.data;
    return this.layersCache;
  }

  async loadCategories(layerCode: string): Promise<TaxonomyCategoryNode[]> {
    const cached = this.categoriesCache.get(layerCode);
    if (cached) {
      return cached;
    }

    const response = await apiGet<TaxonomyCategoryResponseItem[]>(
      `/taxonomy/layers/${encodeURIComponent(layerCode)}/categories`
    );

    const categories = response.data
      .map(category => ({
        code: category.code,
        name: category.name,
        numericCode: normalizeNumericCode(category.numericCode),
        subcategories: [],
      }))
      .sort(compareByCodeAndNumber);

    this.categoriesCache.set(layerCode, categories);
    return categories;
  }

  async loadSubcategories(
    layerCode: string,
    categoryCode: string
  ): Promise<TaxonomySubcategoryNode[]> {
    const cacheKey = `${layerCode}:${categoryCode}`;
    const cached = this.subcategoriesCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const response = await apiGet<TaxonomySubcategoryResponseItem[]>(
      `/taxonomy/layers/${encodeURIComponent(
        layerCode
      )}/categories/${encodeURIComponent(categoryCode)}/subcategories`
    );

    const subcategories = response.data
      .map(subcategory => ({
        code: subcategory.code,
        name: subcategory.name,
        numericCode: normalizeNumericCode(subcategory.numericCode),
      }))
      .sort(compareByCodeAndNumber);

    this.subcategoriesCache.set(cacheKey, subcategories);
    return subcategories;
  }

  private async preloadLayerSubcategories(
    layerCode: string,
    categories: TaxonomyCategoryNode[],
    maxConcurrentSubcategoryRequests: number
  ): Promise<void> {
    for (
      let categoryIndex = 0;
      categoryIndex < categories.length;
      categoryIndex += maxConcurrentSubcategoryRequests
    ) {
      const categoryBatch = categories.slice(
        categoryIndex,
        categoryIndex + maxConcurrentSubcategoryRequests
      );

      await Promise.all(
        categoryBatch.map(category =>
          this.loadSubcategories(layerCode, category.code)
        )
      );
    }
  }

  async preloadAll(
    options: TaxonomyPreloadOptions = {}
  ): Promise<TaxonomyLayerNode[]> {
    const requestedConcurrency =
      options.maxConcurrentSubcategoryRequests ??
      TaxonomyTreeService.DEFAULT_SUBCATEGORY_PRELOAD_CONCURRENCY;
    const maxConcurrentSubcategoryRequests = Number.isFinite(
      requestedConcurrency
    )
      ? Math.max(1, Math.floor(requestedConcurrency))
      : TaxonomyTreeService.DEFAULT_SUBCATEGORY_PRELOAD_CONCURRENCY;
    const layers = await this.loadLayers();
    const layerCodes = sortLayerCodes(Object.keys(layers));

    for (const layerCode of layerCodes) {
      const categories = await this.loadCategories(layerCode);

      await this.preloadLayerSubcategories(
        layerCode,
        categories,
        maxConcurrentSubcategoryRequests
      );
    }

    return this.buildLoadedTree();
  }

  async loadTree(): Promise<TaxonomyTreeResponse> {
    if (this.taxonomyTreeCache) {
      return this.taxonomyTreeCache;
    }

    const response = await apiGet<TaxonomyTreeResponse>('/taxonomy/tree');
    this.taxonomyTreeCache = response.data;
    return this.taxonomyTreeCache;
  }

  getOrderedLayerEntries(): Array<{ code: string; name: string }> {
    if (!this.layersCache) {
      return [];
    }

    return sortLayerCodes(Object.keys(this.layersCache)).map(code => ({
      code,
      name: this.layersCache?.[code] || code,
    }));
  }

  buildLoadedTree(): TaxonomyLayerNode[] {
    if (!this.layersCache) {
      return [];
    }

    return this.getOrderedLayerEntries().map(layer => {
      const categories = this.categoriesCache.get(layer.code) ?? [];

      const categoryNodes = categories.map(category => {
        const subcategories =
          this.subcategoriesCache.get(`${layer.code}:${category.code}`) ?? [];

        return {
          ...category,
          subcategories,
        };
      });

      return {
        ...layer,
        categories: categoryNodes,
      };
    });
  }

  clearCache(): void {
    this.layersCache = null;
    this.categoriesCache.clear();
    this.subcategoriesCache.clear();
    this.taxonomyTreeCache = null;
  }
}

export const taxonomyTreeService = new TaxonomyTreeService();
